import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TGatewaySelectProps } from '@/console/features/gateways/components/gateway-select/gateway-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadGatewaysForSelectQuery } from '@/shared/redux/api/assets/gateways/gateways-api';
import {
  GatewayForSelectFragment,
  ReadGatewaysForSelectQuery,
} from '@/shared/types/types.generated';

export const GatewaySelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  gatewayFilter,
  getItemId,
  createOption,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  multiple = true,
}: TGatewaySelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (gateway: GatewayForSelectFragment) => {
    return {
      label: gateway.name,
      value: gateway.gatewayId,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;
  const transformData = (data?: ReadGatewaysForSelectQuery) => {
    if (!data) {
      return [];
    }

    const gateways = data.readAllGateways.items;
    const validGateways = gatewayFilter
      ? gateways.filter((gateway) => gatewayFilter(gateway))
      : gateways;

    const options = validGateways.map((gateway) => _createOption(gateway));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<
      TFieldValues,
      ReadGatewaysForSelectQuery,
      TOptionShape
    >
      name={name}
      label={t('gateways:select_gateways', { count: multiple ? 2 : 1 })}
      placeholder={t('gateways:search_gateways', { count: multiple ? 2 : 1 })}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      multiple={multiple}
      required={true}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadGatewaysForSelectQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
    />
  );
};
